import { ThemeEvents } from '@theme/events';

/**
 * Recharge v2 encapsule le widget en Shadow DOM : les sélecteurs « plats » ne voient pas
 * `.rc-widget__root` ni les prix. On traverse les shadow roots ouverts récursivement.
 *
 * @param {ParentNode | null | undefined} root
 * @param {string} selector
 * @returns {Element | null}
 */
function querySelectorDeep(root, selector) {
  if (!root) return null;
  try {
    const direct = root.querySelector(selector);
    if (direct) return direct;
  } catch {
    return null;
  }
  const candidates = root.querySelectorAll('*');
  for (const el of candidates) {
    if (el.shadowRoot) {
      const found = querySelectorDeep(el.shadowRoot, selector);
      if (found) return found;
    }
  }
  return null;
}

/** Racine du widget Recharge v2 (souvent à l’intérieur de `<recharge-subscription-widget>`). */
const RC_WIDGET_ROOT = '.rc-widget__root';
/** Arrêt de l’attente si le widget n’apparaît pas (évite un MutationObserver sur body infini). */
const RC_DOM_WAIT_MS = 25000;
/** Recharge met à jour les prix après le rendu Shopify : plusieurs passes + debounce sur l’observer. */
const RC_SYNC_DEBOUNCE_MS = 60;

/**
 * @param {string | null | undefined} text
 * @returns {string}
 */
function normalizePriceText(text) {
  if (!text) return '';
  return text.replace(/\s+/g, ' ').trim();
}

/**
 * Recharge concatène parfois des libellés (souvent pour l’accessibilité) dans le même
 * nœud que le montant — on les retire pour n’afficher que le prix.
 * @param {string | null | undefined} text
 * @returns {string}
 */
function stripRechargePriceNoise(text) {
  if (!text) return '';
  return normalizePriceText(
    text
      .replace(/,?\s*Discounted price:\s*/gi, '')
      .replace(/,?\s*Original price:\s*/gi, '')
      .replace(/,?\s*Prix réduit\s*:\s*/gi, '')
      .replace(/,?\s*Prix d['\u2019]origine\s*:\s*/gi, '')
  );
}

class ProductPrice extends HTMLElement {
  constructor() {
    super();
    /** @type {number[]} */
    this._variantSyncTimeouts = [];
  }

  connectedCallback() {
    // Ne pas dépendre de `.shopify-section` : le thème peut n’exposer que `section.l-pdp`, etc.
    const closestSection = this.closest('section, dialog, .shopify-section, [id^="shopify-section-"]');
    if (!closestSection) return;

    this._sectionEl = closestSection;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    this.waitForRechargeWidget();
  }

  disconnectedCallback() {
    const closestSection = this._sectionEl || this.closest('section, dialog, .shopify-section, [id^="shopify-section-"]');
    if (closestSection) {
      closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    }
    this._clearRechargeDomWait();
    this._clearPendingRechargeSync();
    this._teardownRechargeListeners();
    this._sectionEl = null;
  }

  _clearRechargeDomWait() {
    if (this._domObserver) {
      this._domObserver.disconnect();
      this._domObserver = null;
    }
    if (this._rcDomWaitTimer) {
      clearTimeout(this._rcDomWaitTimer);
      this._rcDomWaitTimer = null;
    }
  }

  _clearPendingRechargeSync() {
    if (this._rcSyncDebounceTimer) {
      clearTimeout(this._rcSyncDebounceTimer);
      this._rcSyncDebounceTimer = null;
    }
    if (this._variantSyncTimeouts?.length) {
      for (const id of this._variantSyncTimeouts) {
        clearTimeout(id);
      }
      this._variantSyncTimeouts = [];
    }
  }

  _teardownRechargeListeners() {
    if (this._rcObserver) {
      this._rcObserver.disconnect();
      this._rcObserver = null;
    }
    if (this._rechargeWidgetEl && this._rechargeChangeHandler) {
      this._rechargeWidgetEl.removeEventListener('change', this._rechargeChangeHandler);
    }
    this._rechargeWidgetEl = null;
    this._rechargeChangeHandler = null;
  }

  /** @returns {HTMLElement | null} */
  findRechargeRoot() {
    const scope = this._sectionEl || this.closest('section, dialog, .shopify-section, [id^="shopify-section-"]');
    let node = scope ? querySelectorDeep(scope, RC_WIDGET_ROOT) : null;
    if (!node && scope !== document.body) {
      node = querySelectorDeep(document.body, RC_WIDGET_ROOT);
    }
    return node instanceof HTMLElement ? node : null;
  }

  /**
   * Aligné sur product-form.js : le `productId` vient du détail d’événement, pas de `event.target`
   * (souvent le variant-picker), pour éviter des rejets erronés au premier changement.
   *
   * @param {CustomEvent} event
   */
  updatePrice = (event) => {
    const data = event.detail?.data;
    if (!data) return;

    if (data.newProduct) {
      this.dataset.productId = data.newProduct.id;
    } else if (data.productId && data.productId !== this.dataset.productId) {
      return;
    }

    const newPrice = data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');
    if (!newPrice || !currentPrice) return;
    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
    }

    // Source fiable et synchrone (données Shopify), contrairement au DOM du widget Recharge
    // qui se met à jour de façon asynchrone et peut encore refléter la variante précédente.
    const resource = event.detail?.resource;
    this._hasSubscriptionOptions = Array.isArray(resource?.selling_plan_allocations)
      ? resource.selling_plan_allocations.length > 0
      : true;

    if (!this._hasSubscriptionOptions) {
      // Variante sans abonnement : le prix Shopify qu'on vient d'injecter est déjà le bon.
      // On n'interroge pas le widget Recharge, qui peut encore afficher la sélection
      // de la variante précédente pendant qu'il se met à jour (source du bug de prix figé/erroné).
      this._clearPendingRechargeSync();
      this.resetRechargeUI();
      return;
    }

    // On programme les relectures même si le widget est introuvable à l'instant présent :
    // en venant d'une variante sans abonnement, Recharge peut être en plein démontage/
    // reconstruction de son widget au moment précis du clic (il n'existe alors dans le DOM
    // sous aucune forme). Attendre qu'il existe déjà avant de programmer les relectures
    // revient à ne jamais les programmer dans ce cas précis.
    this._queueRechargeSyncAfterVariantChange();
  };

  /**
   * Réinitialise l'affichage prix injecté précédemment par Recharge (ancien prix barré,
   * badge d'économie) quand la variante courante n'a pas d'abonnement.
   */
  resetRechargeUI() {
    const priceContainer = this.querySelector('[ref="priceContainer"]');
    if (!(priceContainer instanceof HTMLElement)) return;

    const oldEl = priceContainer.querySelector('.c-price--old');
    if (oldEl instanceof HTMLElement) {
      oldEl.style.display = 'none';
      oldEl.hidden = true;
    }

    const ecoEl = this.querySelector('.c-price__eco');
    if (ecoEl instanceof HTMLElement) {
      ecoEl.style.display = 'none';
      ecoEl.hidden = true;
    }
  }

  /**
   * Recharge rafraîchit les montants après le fragment Shopify : plusieurs passes courtes.
   * Le noeud widget n'est jamais mis en cache ici : il est retrouvé à chaque passe (voir `run`),
   * car Recharge peut le démonter/reconstruire entièrement entre deux passes, voire ne pas
   * exister du tout au moment où cette méthode est appelée.
   */
  _queueRechargeSyncAfterVariantChange() {
    this._clearPendingRechargeSync();
    this._variantSyncTimeouts = [];

    const run = () => {
      const currentWidget = this.findRechargeRoot();
      // Recharge est peut-être toujours en train de démonter/reconstruire son widget
      // (aucune trace dans le DOM pour l'instant) : rien à lire, une prochaine passe retentera.
      if (!currentWidget) return;

      // Recharge remplace parfois entièrement son noeud racine (`.rc-widget__root`) au lieu
      // de le mettre à jour en place — typiquement quand il reconstruit son groupe d'options
      // après être passé par une variante sans abonnement. Si le noeud a changé depuis la
      // dernière fois, on ré-attache écouteur + MutationObserver dessus (initRechargeListener),
      // sans quoi les passes suivantes liraient un DOM mort (détaché du document).
      if (currentWidget !== this._rechargeWidgetEl) {
        this.initRechargeListener(currentWidget);
        return;
      }
      this.syncFromRecharge(currentWidget);
    };

    // Passes tardives ajoutées : quand la variante précédente n'avait pas d'abonnement,
    // Recharge doit reconstruire tout son groupe d'options (et son noeud racine) depuis zéro.
    // Mesuré en conditions réelles : ça peut prendre de ~1,5s à plus de 4,5s selon les cas.
    // Sans ces passes tardives, une lecture intermédiaire (widget pas encore reconstruit)
    // reste affichée indéfiniment, faute d'une passe ultérieure pour la corriger.
    for (const ms of [0, 16, 48, 100, 200, 350, 500, 750, 1000, 1500, 2000, 2750, 3500, 4500, 5500, 7000, 9000]) {
      this._variantSyncTimeouts.push(setTimeout(run, ms));
    }
  }

  /**
   * @param {HTMLElement} widget
   */
  _scheduleDebouncedRechargeSync(widget) {
    if (this._rcSyncDebounceTimer) {
      clearTimeout(this._rcSyncDebounceTimer);
    }
    this._rcSyncDebounceTimer = setTimeout(() => {
      this._rcSyncDebounceTimer = null;
      this.syncFromRecharge(widget);
    }, RC_SYNC_DEBOUNCE_MS);
  }

  waitForRechargeWidget() {
    const existing = this.findRechargeRoot();
    if (existing) {
      this.initRechargeListener(existing);
      return;
    }

    this._domObserver = new MutationObserver(() => {
      const widget = this.findRechargeRoot();
      if (!widget) return;
      this._clearRechargeDomWait();
      this.initRechargeListener(widget);
    });

    this._domObserver.observe(document.body, { childList: true, subtree: true });

    this._rcDomWaitTimer = setTimeout(() => {
      this._clearRechargeDomWait();
    }, RC_DOM_WAIT_MS);
  }

  /**
   * @param {HTMLElement} widget
   */
  initRechargeListener(widget) {
    this._teardownRechargeListeners();
    this._rechargeWidgetEl = widget;

    const onWidgetMutation = () => {
      this._scheduleDebouncedRechargeSync(widget);
    };

    const onWidgetChange = () => {
      this.syncFromRecharge(widget);
      this._scheduleDebouncedRechargeSync(widget);
    };

    this._rechargeChangeHandler = onWidgetChange;

    this._rcObserver = new MutationObserver(onWidgetMutation);
    this._rcObserver.observe(widget, {
      subtree: true,
      attributes: true,
      attributeFilter: ['rc-selected', 'rc-not-selected'],
      childList: true,
      characterData: true,
    });

    widget.addEventListener('change', onWidgetChange);

    this.syncFromRecharge(widget);
  }

  /**
   * Ne pas utiliser `.c-price` seul : `.c-price--old` matcherait aussi.
   * @param {HTMLElement} container
   * @returns {HTMLElement | null}
   */
  getMainPriceEl(container) {
    const discount = container.querySelector('.c-price--discount');
    if (discount instanceof HTMLElement) return discount;
    const regular = container.querySelector('.c-price.price:not(.c-price--old)');
    if (regular instanceof HTMLElement) return regular;
    const fallback = container.querySelector('.c-price:not(.c-price--old)');
    return fallback instanceof HTMLElement ? fallback : null;
  }

  /**
   * @param {HTMLElement} el
   * @returns {string[]}
   */
  getPriceSizeClasses(el) {
    return ['c-price--sm', 'c-price--md', 'c-price--lg'].filter((c) => el.classList.contains(c));
  }

  /**
   * @param {HTMLElement} widget
   */
  syncFromRecharge(widget) {
    // Garde-fou : ignore toute lecture du widget (retry planifié ou MutationObserver)
    // qui arriverait en retard alors qu'on est déjà sur une variante sans abonnement.
    if (this._hasSubscriptionOptions === false) return;

    const priceContainerNode = this.querySelector('[ref="priceContainer"]');
    if (!(priceContainerNode instanceof HTMLElement)) return;
    const priceContainer = priceContainerNode;

    const selected = querySelectorDeep(widget, '.rc-purchase-option[rc-selected]');
    if (!selected) return;

    const discountedPart =
      querySelectorDeep(selected, '[part~="rc-purchase-option__discounted-price"]') ||
      querySelectorDeep(selected, '.rc-purchase-option__discounted-price');

    const isSubscription =
      selected.classList.contains('rc-purchase-option__subscription') ||
      Boolean(discountedPart && normalizePriceText(discountedPart.textContent));

    let mainPriceText;
    let oldPriceText;

    if (isSubscription) {
      mainPriceText = stripRechargePriceNoise(discountedPart?.textContent);
      const originalEl =
        querySelectorDeep(selected, '[part~="rc-purchase-option__original-price"]') ||
        querySelectorDeep(selected, '.rc-purchase-option__original-price');
      oldPriceText = stripRechargePriceNoise(originalEl?.textContent);
    } else {
      const oneTimeEl =
        querySelectorDeep(selected, '[part~="rc-purchase-option__price"]') ||
        querySelectorDeep(selected, '.rc-purchase-option__price');
      mainPriceText = stripRechargePriceNoise(oneTimeEl?.textContent);
    }

    if (!mainPriceText) return;

    const priceEl = this.getMainPriceEl(priceContainer);
    if (!priceEl) return;

    const sizeClasses = this.getPriceSizeClasses(priceEl);

    // Prix barré Shopify identifié via data-attribute posé côté Liquid (jamais écrasé par JS)
    const shopifyCompareEl = priceContainer.querySelector('[data-shopify-compare]');

    if (isSubscription) {
      priceEl.className = ['c-price', 'c-price--discount', ...sizeClasses].join(' ').trim();
      priceEl.textContent = mainPriceText;
    } else {
      // Achat unique : conserver le style discount si la variante a un prix barré Shopify
      priceEl.className = [
        'c-price',
        shopifyCompareEl ? 'c-price--discount' : 'price',
        ...sizeClasses,
      ].join(' ').trim();
      priceEl.textContent = mainPriceText;
    }

    const oldElNode = priceContainer.querySelector('.c-price--old');
    let oldEl = oldElNode instanceof HTMLElement ? oldElNode : null;

    if (isSubscription && oldPriceText) {
      if (!oldEl) {
        oldEl = document.createElement('span');
        oldEl.className = ['c-price', 'c-price--old', ...sizeClasses].join(' ').trim();
        priceContainer.insertBefore(oldEl, priceEl);
      } else {
        oldEl.className = ['c-price', 'c-price--old', ...sizeClasses].join(' ').trim();
      }
      oldEl.textContent = oldPriceText;
      oldEl.hidden = false;
      oldEl.style.display = '';
    } else {
      // On n'est pas dans le cas abonnement + prix barré : masquer tout `oldEl` restant
      // (ex. laissé par une lecture précédente prise en pleine reconstruction du widget),
      // qu'un prix barré Shopify natif soit affiché ensuite ou non.
      if (oldEl) {
        oldEl.style.display = 'none';
        oldEl.hidden = true;
      }

      if (!isSubscription && shopifyCompareEl) {
        // Achat unique : restaurer le prix barré Shopify depuis le data-attribute (texte immuable)
        shopifyCompareEl.textContent = shopifyCompareEl.dataset.shopifyCompare;
        shopifyCompareEl.hidden = false;
        shopifyCompareEl.style.display = '';
      }
    }

    const badge = querySelectorDeep(selected, '.rc-purchase-option__badge');
    const badgeText = badge?.textContent ? normalizePriceText(badge.textContent) : '';

    const ecoNode = this.querySelector('.c-price__eco');
    let ecoEl = ecoNode instanceof HTMLElement ? ecoNode : null;
    if (isSubscription && (oldPriceText || badgeText)) {
      if (!ecoEl) {
        ecoEl = document.createElement('span');
        ecoEl.className = 'c-price__eco u-txt-sm';
        this.appendChild(ecoEl);
      }
      ecoEl.textContent = badgeText;
      ecoEl.hidden = !badgeText;
      ecoEl.style.display = badgeText ? '' : 'none';
    } else if (ecoEl) {
      ecoEl.style.display = 'none';
      ecoEl.hidden = true;
    }
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
